import React, { useState } from "react";
import { uploadImage, imageUrl } from "./api";
import { toast } from "sonner";
import { Image, X, Upload, Warning } from "@phosphor-icons/react";

export default function ImageUpload({ value, onChange, label = "Image", testId = "image-upload" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    if (file.size > 5 * 1024 * 1024) {
      const msg = `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum 5 MB — please resize before uploading.`;
      setError(msg); toast.error(msg); e.target.value = ""; return;
    }
    setBusy(true);
    try {
      const res = await uploadImage(file);
      onChange(res.id);
      toast.success("Image uploaded");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      setError(String(detail));
      toast.error(String(detail));
    } finally {
      setBusy(false);
      // reset file input so picking the same file again re-triggers onChange
      if (e && e.target) e.target.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      <div className="text-[12.5px] font-medium text-[#1C2B3A] mb-1.5">{label}</div>
      {value ? (
        <div className="relative inline-block">
          <img src={imageUrl(value).replace("/api/images/", `${process.env.REACT_APP_BACKEND_URL}/api/images/`)}
               alt="" className="hidden" />
          <ImagePreview imageId={value} />
          <button type="button"
            data-testid={`${testId}-remove`}
            onClick={() => onChange(null)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-red-50 hover:border-red-200">
            <X size={12} weight="bold" />
          </button>
        </div>
      ) : (
        <label className={`cursor-pointer inline-flex flex-col items-center justify-center w-full max-w-[300px] aspect-[16/10] rounded-lg border-2 border-dashed ${error ? "border-[#1B6FE0]/60 bg-blue-50/40" : "border-slate-300 bg-slate-50"} hover:border-[#1B6FE0] transition`}>
          <Upload size={22} weight="duotone" className="text-[#6B7A8D]" />
          <span className="mt-2 text-[12.5px] text-[#6B7A8D]">{busy ? "Uploading…" : "Click to upload"}</span>
          <span className="mt-1 text-[10.5px] text-[#94A3B8]">PNG, JPG, WebP · ≥64×64px · up to 5 MB</span>
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={onPick} disabled={busy} data-testid={`${testId}-input`} />
        </label>
      )}
      {error && (
        <div className="mt-2 flex items-start gap-1.5 text-[11.5px]" style={{ color: "#1B6FE0" }} data-testid={`${testId}-error`}>
          <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

function ImagePreview({ imageId }) {
  const [src, setSrc] = useState(null);
  React.useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/images/${imageId}`)
      .then((r) => r.json()).then((j) => { if (!cancelled) setSrc(j.data_url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [imageId]);
  if (!src) {
    return <div className="w-[200px] aspect-[16/10] rounded-lg bg-slate-100 flex items-center justify-center text-[#6B7A8D]"><Image size={22} weight="duotone" /></div>;
  }
  return <img src={src} alt="" className="w-[200px] aspect-[16/10] rounded-lg object-cover border border-slate-200" />;
}
